import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Upload, X, Save, AlertCircle, CheckCircle, ArrowLeft } from "lucide-react";
import Papa from "papaparse";
import { handlePost, handleGet } from "../../../controllers/controller.all";
import { useNavigate } from "react-router-dom";
import SubmitButton from "../../../components/ui/SubmitButton";

export default function BatchUpload({ role }) {
    const navigate = useNavigate();
    const user = useSelector((state) => state.userReducer?.user);
    const token = user?.access_token || "";
    const ecoleId = user?.ecole?._id || user?.ecole;
    
    const [file, setFile] = useState(null);
    const [data, setData] = useState([]);
    const [sendNotification, setSendNotification] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [detailedErrors, setDetailedErrors] = useState([]);
    const [warning, setWarning] = useState(null);
    const [hasMissingColumns, setHasMissingColumns] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [classes, setClasses] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [bulkClass, setBulkClass] = useState("");

    useEffect(() => {
        if (role === "ETUDIANT" && token) {
            handleGet(`/classe/ecole/${ecoleId}?skip=0&limit=100000`, (res) => {
                if (res?.classes) {
                    setClasses(res.classes);
                }
            }, token);
        }
    }, [role, ecoleId, token]);

    const roleName = role === "ETUDIANT" ? "Étudiants" : role === "PROFESSEUR" ? "Professeurs" : "Administrateurs";
    const backLink = role === "ETUDIANT" ? "/students" : role === "PROFESSEUR" ? "/teachers" : "/admins";

    const handleFileUpload = (e) => {
        const uploadedFile = e.target.files[0];
        if (uploadedFile) {
            setFile(uploadedFile);
            Papa.parse(uploadedFile, {
                header: true,
                skipEmptyLines: true,
                complete: (results) => {
                    const expectedColumns = role === "ETUDIANT" 
                        ? ["prenom", "nom", "email", "tel", "adresse", "sexe", "date_naissance", "classe", "tuteur_nom", "tuteur_prenom", "tuteur_tel", "tuteur_email"] 
                        : ["prenom", "nom", "email", "tel", "adresse", "sexe", "date_naissance"];
                    
                    const actualColumns = results.meta.fields || [];
                    const missingColumns = expectedColumns.filter(c => !actualColumns.includes(c));
                    const extraColumns = actualColumns.filter(c => !expectedColumns.includes(c));

                    if (missingColumns.length > 0 || extraColumns.length > 0) {
                        let warnMsg = "Attention: Le format du fichier CSV diffère du modèle attendu.";
                        if (missingColumns.length > 0) {
                            warnMsg += ` Colonnes manquantes: ${missingColumns.join(', ')}. (La sauvegarde est désactivée)`;
                            setHasMissingColumns(true);
                        } else {
                            setHasMissingColumns(false);
                        }
                        if (extraColumns.length > 0) warnMsg += ` Colonnes inattendues: ${extraColumns.join(', ')}.`;
                        setWarning(warnMsg);
                    } else {
                        setWarning(null);
                        setHasMissingColumns(false);
                    }

                    // Add an internal ID for tracking edits
                    const parsedData = results.data.map((row, index) => ({
                        ...row,
                        _tempId: index,
                        role: role,
                        ecole: ecoleId,
                        // Set defaults if empty
                        email: row.email || "",
                        tel: row.tel || "",
                        prenom: row.prenom || "",
                        nom: row.nom || "",
                        adresse: row.adresse || "",
                        date_naissance: row.date_naissance || "",
                        classe: row.classe || "",
                        tuteur_nom: row.tuteur_nom || "",
                        tuteur_prenom: row.tuteur_prenom || "",
                        tuteur_tel: row.tuteur_tel || "",
                        tuteur_email: row.tuteur_email || "",
                        sexe: row.sexe || "Masculin"
                    }));
                    setData(parsedData);
                    setError(null);
                },
                error: (error) => {
                    setError("Erreur lors de la lecture du fichier CSV: " + error.message);
                    setWarning(null);
                }
            });
        }
    };

    const handleCellEdit = (index, field, value) => {
        const newData = [...data];
        newData[index][field] = value;
        setData(newData);
    };

    const toggleRowSelection = (index) => {
        setSelectedRows(prev => prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]);
    };

    const toggleAllSelection = () => {
        if (selectedRows.length === data.length) {
            setSelectedRows([]);
        } else {
            setSelectedRows(data.map((_, i) => i));
        }
    };

    const applyBulkClass = () => {
        if (!bulkClass || selectedRows.length === 0) return;
        const newData = [...data];
        selectedRows.forEach(index => {
            newData[index].classe = bulkClass;
        });
        setData(newData);
        setSelectedRows([]);
        setBulkClass("");
    };

    const removeRow = (index) => {
        setData(data.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {
        if (data.length === 0) return;
        setLoading(true);
        setError(null);
        setSuccessMessage("");

        const payload = data.map(({ _tempId, sexe, tuteur_nom, tuteur_prenom, tuteur_tel, tuteur_email, ...rest }) => {
            const userPayload = { ...rest, genre: sexe };
            if (role === "ETUDIANT") {
                if (tuteur_nom || tuteur_prenom || tuteur_tel || tuteur_email) {
                    userPayload.tuteurs = [{
                        nom: tuteur_nom || "",
                        prenom: tuteur_prenom || "",
                        tel: tuteur_tel || "",
                        email: tuteur_email || ""
                    }];
                } else {
                    userPayload.tuteurs = [];
                }
                
                // Map the string class from CSV to an actual ID if we find a match by libelle,
                // or keep the ID if they selected from dropdown
                if (userPayload.classe && typeof userPayload.classe === 'string') {
                    const matchedClass = classes.find(c => c.libelle?.toLowerCase() === userPayload.classe.toLowerCase() || c._id === userPayload.classe);
                    if (matchedClass) {
                        userPayload.classe = matchedClass._id;
                    }
                }
            }
            return userPayload;
        });

        let shouldKeepLoading = false;
        try {
            await handlePost(`/user/batch?send_notification=${sendNotification}`, payload, (res) => {
                if (res?.state === "success" || res?.state === "warning") {
                    setSuccessMessage(res.message);
                    if (res.errors && res.errors.length > 0) {
                        setError(`${res.errors.length} compte(s) n'ont pas pu être importés (ex: numéro de téléphone déjà existant).`);
                        setDetailedErrors(res.errors);
                    } else {
                        shouldKeepLoading = true;
                        setTimeout(() => navigate(backLink), 2000);
                    }
                } else {
                    setError(res?.message || "Erreur inconnue");
                }
            }, token, false);
        } catch (err) {
            setError("Erreur de connexion avec le serveur.");
        } finally {
            if (!shouldKeepLoading) setLoading(false);
        }
    };

    const generateTemplate = () => {
        const columns = role === "ETUDIANT" 
            ? ["prenom", "nom", "email", "tel", "adresse", "sexe", "date_naissance", "classe", "tuteur_nom", "tuteur_prenom", "tuteur_tel", "tuteur_email"] 
            : ["prenom", "nom", "email", "tel", "adresse", "sexe", "date_naissance"];
        
        const csv = Papa.unparse({
            fields: columns,
            data: []
        });
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `modele_${role.toLowerCase()}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex items-center gap-4">
                <button onClick={() => navigate(backLink)} className="p-2 hover:bg-gray-100 rounded-lg transition text-gray-600">
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Import en masse : {roleName}</h1>
                    <p className="text-sm text-gray-500 mt-1">Importez plusieurs utilisateurs à la fois depuis un fichier CSV.</p>
                </div>
            </div>

            {/* Info Alert */}
            <div className="bg-indigo-50 border border-indigo-200 text-indigo-800 p-4 rounded-xl flex items-start gap-3">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                    <p className="font-semibold mb-1">Comment ça marche ?</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Téléchargez le modèle CSV en utilisant le bouton ci-dessous.</li>
                        <li>Remplissez le fichier avec les informations (sans modifier les entêtes de colonnes).</li>
                        <li>Le mot de passe par défaut est généré automatiquement selon le format : <b>Nom + prenom + 123&</b> (avec la première lettre du nom en majuscule, ex: <i>Yameogoivan123&</i>).</li>
                        {role === "ETUDIANT" && (
                            <>
                                <li>Pour les étudiants, <b>seul 1 tuteur (principal)</b> peut être importé via ce fichier. Les autres pourront être ajoutés manuellement sur le profil.</li>
                                <li>Le champ "Matricule" n'est plus requis dans le CSV, le système le générera automatiquement (ex: KL2039...).</li>
                                <li>Vous pourrez réajuster la classe de chaque élève directement dans le tableau avant la sauvegarde.</li>
                            </>
                        )}
                    </ul>
                </div>
            </div>

            {/* Upload Area */}
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                <div className="flex flex-col sm:flex-row items-center gap-4 justify-between">
                    <div>
                        <input
                            type="file"
                            accept=".csv"
                            onChange={handleFileUpload}
                            className="hidden"
                            id="csv-upload"
                        />
                        <label 
                            htmlFor="csv-upload" 
                            className="cursor-pointer inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 transition"
                        >
                            <Upload className="w-4 h-4" /> Sélectionner un fichier CSV
                        </label>
                        {file && <span className="ml-3 text-sm text-gray-600">{file.name}</span>}
                    </div>
                    
                    <button 
                        onClick={generateTemplate}
                        className="text-indigo-600 hover:text-indigo-800 text-sm font-medium transition"
                    >
                        Télécharger le modèle CSV
                    </button>
                </div>
            </div>

            {/* Warning Message */}
            {warning && (
                <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-4 rounded-xl text-sm flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <span>{warning}</span>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl text-sm flex flex-col gap-2">
                    <div className="flex items-start gap-2 font-medium">
                        <AlertCircle className="w-5 h-5 flex-shrink-0" />
                        <span>{error}</span>
                    </div>
                    {detailedErrors.length > 0 && (
                        <ul className="list-disc list-inside mt-2 space-y-1 text-xs">
                            {detailedErrors.map((err, idx) => {
                                const isDuplicate = err.error && (err.error.includes("duplicate key") || err.error.includes("E11000"));
                                return (
                                    <li key={idx}>
                                        <strong>Contact : {err.numero}</strong> - {isDuplicate ? "Ce numéro de téléphone est déjà utilisé." : err.error}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            )}

            {/* Success Message */}
            {successMessage && (
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 p-4 rounded-xl text-sm flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 flex-shrink-0" />
                    <span>{successMessage}</span>
                </div>
            )}

            {/* Preview and Edit Table */}
            {data.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col">
                    <div className="p-4 border-b border-gray-200 bg-gray-50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="font-medium text-gray-900 flex items-center gap-4">
                            <span>Prévisualisation ({data.length} entrées)</span>
                            {role === "ETUDIANT" && selectedRows.length > 0 && (
                                <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 px-3 py-1.5 rounded-lg">
                                    <select
                                        value={bulkClass}
                                        onChange={(e) => setBulkClass(e.target.value)}
                                        className="text-sm bg-white border border-indigo-300 rounded p-1"
                                    >
                                        <option value="">Sélectionner une classe</option>
                                        {classes.map(c => (
                                            <option key={c._id} value={c._id}>{c.libelle}</option>
                                        ))}
                                    </select>
                                    <button 
                                        onClick={applyBulkClass}
                                        className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white px-2 py-1 rounded transition"
                                    >
                                        Appliquer ({selectedRows.length})
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <input 
                                type="checkbox" 
                                id="sendNotif"
                                checked={sendNotification}
                                onChange={(e) => setSendNotification(e.target.checked)}
                                className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                            />
                            <label htmlFor="sendNotif" className="text-sm text-gray-700 select-none cursor-pointer">
                                Envoyer un email / SMS de bienvenue
                            </label>
                        </div>
                    </div>
                    
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    {role === "ETUDIANT" && (
                                        <th className="px-4 py-3 text-left">
                                            <input 
                                                type="checkbox" 
                                                checked={selectedRows.length === data.length && data.length > 0}
                                                onChange={toggleAllSelection}
                                                className="rounded border-gray-300 text-indigo-600 shadow-sm"
                                            />
                                        </th>
                                    )}
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prénom</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Téléphone</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]">Sexe</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date Naissance</th>
                                    {role === "ETUDIANT" && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[150px]">Classe</th>}
                                    {role === "ETUDIANT" && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px]">Tuteur (Nom - Tel)</th>}
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {data.map((row, index) => (
                                    <tr key={row._tempId} className="hover:bg-gray-50">
                                        {role === "ETUDIANT" && (
                                            <td className="px-4 py-2">
                                                <input 
                                                    type="checkbox" 
                                                    checked={selectedRows.includes(index)}
                                                    onChange={() => toggleRowSelection(index)}
                                                    className="rounded border-gray-300 text-indigo-600 shadow-sm"
                                                />
                                            </td>
                                        )}
                                        <td className="px-4 py-2">
                                            <input 
                                                type="text" 
                                                value={row.prenom} 
                                                onChange={(e) => handleCellEdit(index, 'prenom', e.target.value)}
                                                className="w-full bg-transparent border-none p-1 focus:ring-1 focus:ring-indigo-500 rounded text-sm"
                                            />
                                        </td>
                                        <td className="px-4 py-2">
                                            <input 
                                                type="text" 
                                                value={row.nom} 
                                                onChange={(e) => handleCellEdit(index, 'nom', e.target.value)}
                                                className="w-full bg-transparent border-none p-1 focus:ring-1 focus:ring-indigo-500 rounded text-sm"
                                            />
                                        </td>
                                        <td className="px-4 py-2">
                                            <input 
                                                type="email" 
                                                value={row.email} 
                                                onChange={(e) => handleCellEdit(index, 'email', e.target.value)}
                                                className="w-full bg-transparent border-none p-1 focus:ring-1 focus:ring-indigo-500 rounded text-sm"
                                            />
                                        </td>
                                        <td className="px-4 py-2">
                                            <input 
                                                type="tel" 
                                                value={row.tel} 
                                                onChange={(e) => handleCellEdit(index, 'tel', e.target.value)}
                                                className="w-full bg-transparent border-none p-1 focus:ring-1 focus:ring-indigo-500 rounded text-sm w-24"
                                            />
                                        </td>
                                        <td className="px-4 py-2">
                                            <select 
                                                value={row.sexe} 
                                                onChange={(e) => handleCellEdit(index, 'sexe', e.target.value)}
                                                className="bg-transparent border-none p-1 focus:ring-1 focus:ring-indigo-500 rounded text-sm"
                                            >
                                                <option value="Masculin">Masculin</option>
                                                <option value="Féminin">Féminin</option>
                                            </select>
                                        </td>
                                        <td className="px-4 py-2">
                                            <input 
                                                type="text" 
                                                value={row.date_naissance || ""} 
                                                placeholder="JJ/MM/AAAA"
                                                onChange={(e) => handleCellEdit(index, 'date_naissance', e.target.value)}
                                                className="w-full bg-transparent border-none p-1 focus:ring-1 focus:ring-indigo-500 rounded text-sm min-w-[100px]"
                                            />
                                        </td>
                                        {role === "ETUDIANT" && (
                                            <>
                                                <td className="px-4 py-2">
                                                    <select
                                                        value={classes.find(c => c._id === row.classe || c.libelle?.toLowerCase() === row.classe?.toLowerCase())?._id || ""}
                                                        onChange={(e) => handleCellEdit(index, 'classe', e.target.value)}
                                                        className="w-full bg-transparent border-none p-1 focus:ring-1 focus:ring-indigo-500 rounded text-sm min-w-[120px]"
                                                    >
                                                        <option value="">Sélectionner...</option>
                                                        {classes.map(c => (
                                                            <option key={c._id} value={c._id}>{c.libelle}</option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td className="px-4 py-2">
                                                    <div className="flex flex-col gap-1 min-w-[150px]">
                                                        <input 
                                                            type="text" 
                                                            value={row.tuteur_nom || ""} 
                                                            placeholder="Nom du tuteur"
                                                            onChange={(e) => handleCellEdit(index, 'tuteur_nom', e.target.value)}
                                                            className="w-full bg-transparent border-none p-1 bg-gray-50 focus:bg-white focus:ring-1 focus:ring-indigo-500 rounded text-xs"
                                                        />
                                                        <input 
                                                            type="text" 
                                                            value={row.tuteur_tel || ""} 
                                                            placeholder="Tel tuteur"
                                                            onChange={(e) => handleCellEdit(index, 'tuteur_tel', e.target.value)}
                                                            className="w-full bg-transparent border-none p-1 bg-gray-50 focus:bg-white focus:ring-1 focus:ring-indigo-500 rounded text-xs"
                                                        />
                                                    </div>
                                                </td>
                                            </>
                                        )}
                                        <td className="px-4 py-2 text-right">
                                            <button 
                                                onClick={() => removeRow(index)}
                                                className="text-red-500 hover:bg-red-50 p-1.5 rounded transition"
                                                title="Retirer cette ligne"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    
                    <div className="p-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
                        <div className="text-sm text-gray-500">
                            Vérifiez les données avant de les sauvegarder. Les modifications dans le tableau ci-dessus sont prises en compte.
                        </div>
                        <SubmitButton 
                            onClick={handleSubmit}
                            isSubmitting={loading}
                            disabled={data.length === 0 || hasMissingColumns}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm"
                            loadingText="Sauvegarde..."
                        >
                            <Save className="w-4 h-4" /> Sauvegarder les imports
                        </SubmitButton>
                    </div>
                </div>
            )}

        </div>
    );
}
